import { useState, useEffect, ReactElement } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { TemperatureDetails, SummerExternalType, SummerExternalColor } from '../../types/domainTypes';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { Autocomplete, FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NumericTextField } from 'components/FormFields/NumericTextField';

interface TemperatureDetailsDialogProps {
  title: string;
  onClose: () => void;
  onSave: (temperatureDetails: TemperatureDetails) => Promise<void>;
  temperatureDetails?: TemperatureDetails;
  temperatureData?: SummerExternalColor[];
}

export type TemperatureDetailsFormData = {
  summerExternalType: SummerExternalType;
  summerExternalColor?: SummerExternalColor;
  summerExternalCustom?: number;
  summerInternal: number;
  winterExternal: number;
  winterInternal: number;
};

export function TemperatureDetailsDialog({
  title,
  onClose,
  onSave,
  temperatureDetails,
  temperatureData,
}: TemperatureDetailsDialogProps): ReactElement {
  const modifiedTemperatureData = temperatureData?.map((data) => {
    return { id: data.id, label: `${data.label} (${data.value} \u00b0C)`, value: data.value };
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty },
  } = useForm<TemperatureDetailsFormData>({
    reValidateMode: 'onChange',
    defaultValues: {
      summerExternalColor: modifiedTemperatureData?.find((item) => item.label.toLowerCase().includes('colour group 3')),
      summerInternal: 25,
      winterExternal: -20,
      winterInternal: 20,
    },
  });
  const { t } = useTranslation();

  const [hasFormSubmitted, setHasFormSubmitted] = useState(false);
  const [summerExternalType, setSummerExternalType] = useState<SummerExternalType | null>(SummerExternalType.SelectColor);
  const isFormInEdit = temperatureDetails !== undefined;

  const handleDialogSave: SubmitHandler<TemperatureDetailsFormData> = async (formData: TemperatureDetailsFormData) => {
    setHasFormSubmitted(true);

    const newTemperatureDetails: TemperatureDetails = {
      summerExternalColor: formData.summerExternalColor,
      summerExternalCustom: formData.summerExternalCustom,
      summerInternal: formData.summerInternal,
      winterExternal: formData.winterExternal,
      winterInternal: formData.winterInternal,
    };

    if (summerExternalType === SummerExternalType.SelectColor) {
      newTemperatureDetails.summerExternalCustom = undefined;
    } else if (summerExternalType === SummerExternalType.EnterCustom) {
      newTemperatureDetails.summerExternalColor = undefined;
    } else {
      return;
    }

    await onSave(newTemperatureDetails);
  };

  useEffect(() => {
    if (temperatureDetails) {
      setValue('summerExternalCustom', temperatureDetails.summerExternalCustom);
      setValue('summerExternalColor', temperatureDetails.summerExternalColor);
      setValue('summerInternal', temperatureDetails.summerInternal);
      setValue('winterExternal', temperatureDetails.winterExternal);
      setValue('winterInternal', temperatureDetails.winterInternal);

      if (temperatureDetails.summerExternalColor) {
        setSummerExternalType(SummerExternalType.SelectColor);
      } else if (temperatureDetails.summerExternalCustom) {
        setSummerExternalType(SummerExternalType.EnterCustom);
      } else {
        setSummerExternalType(null);
      }
    }
  }, [temperatureDetails, setValue]);

  const isRadioGroupError = hasFormSubmitted && !summerExternalType;
  const requiredHelperText = `*${t('common.required-helper-text')}`;
  const requiredError = t('common.required-error-text');

  return (
    <EditDialog
      saveActionText={isFormInEdit ? undefined : t('calculation-page.temperature-details-dialog.save-action-text')}
      title={title}
      onClose={onClose}
      handleFormSubmit={handleSubmit(handleDialogSave)}
      defaultErrorMessage={'calculation-page.temperature-details-dialog.default-error-message'}
      isDirty={isDirty}
    >
      <Stack spacing={5}>
        <FormControl data-testid="temperature-details-form">
          <FormLabel error={isRadioGroupError} component="legend">
            {t('calculation-page.temperature-details-dialog.radio-group-label')}
          </FormLabel>
          <RadioGroup
            value={summerExternalType}
            style={{ margin: 0 }}
            data-testid="temperature-details-form-radio-group"
            onChange={(e) => {
              setSummerExternalType(e.target.value as SummerExternalType);
              setHasFormSubmitted(false);
            }}
          >
            <Stack spacing={1}>
              <Stack spacing={1}>
                <FormControlLabel
                  value={SummerExternalType.SelectColor}
                  control={<Radio />}
                  label={t('calculation-page.temperature-details-dialog.radio-select-color')}
                />
                {summerExternalType === SummerExternalType.SelectColor && (
                  <Controller
                    name="summerExternalColor"
                    control={control}
                    rules={{
                      validate: {
                        required: (v) => !!v,
                      },
                    }}
                    render={(props) => {
                      const helperText: string = props.fieldState.invalid ? requiredError : requiredHelperText;
                      return (
                        <Autocomplete
                          isOptionEqualToValue={(option, value) => option.label === value.label}
                          disablePortal
                          id="temperature-details-form-summer-external-combo"
                          fullWidth
                          value={props.field.value}
                          options={modifiedTemperatureData as unknown as SummerExternalColor[]}
                          data-testid="temperature-details-form-summer-external-autocomplete"
                          renderInput={(params) => (
                            <TextField
                              data-testid="temperature-details-form-summer-external-autocomplete-textfield"
                              error={props.fieldState.invalid}
                              helperText={helperText}
                              {...params}
                              label={t('calculation-page.temperature-details-dialog.label-select-color-textfield')}
                            />
                          )}
                          onChange={(_, data) => props.field.onChange(data)}
                        />
                      );
                    }}
                  />
                )}
              </Stack>
              <Stack spacing={1}>
                <FormControlLabel
                  value={SummerExternalType.EnterCustom}
                  control={<Radio />}
                  label={t('calculation-page.temperature-details-dialog.radio-enter-custom-temperature')}
                />
                {summerExternalType === SummerExternalType.EnterCustom && (
                  <NumericTextField
                    name="summerExternalCustom"
                    control={control}
                    label={t('calculation-page.temperature-details-dialog.label-summer-external-custom')}
                    id="temperature-details-form-summer-external"
                    data-testid="temperature-details-form-summer-external"
                    fullWidth
                    multiline
                    required
                    allowNegative
                    allowZero
                    adornmentProps={{ endAdornment: '\u00b0C' }}
                  />
                )}
              </Stack>
            </Stack>
          </RadioGroup>
          {summerExternalType === null && (
            <FormHelperText error={isRadioGroupError} sx={{ marginLeft: 0 }} id="component-error-text">
              {isRadioGroupError ? requiredError : requiredHelperText}
            </FormHelperText>
          )}
        </FormControl>
        <Stack spacing={3}>
          <NumericTextField
            name="summerInternal"
            control={control}
            required
            label={t('calculation-page.temperature-details-dialog.label-summer-internal')}
            id="temperature-details-form-summer-internal"
            data-testid="temperature-details-form-summer-internal"
            fullWidth
            multiline
            allowNegative
            allowZero
            adornmentProps={{ endAdornment: '\u00b0C' }}
          />
          <NumericTextField
            name="winterExternal"
            control={control}
            required
            label={t('calculation-page.temperature-details-dialog.label-winter-external')}
            id="temperature-details-form-winter-external"
            data-testid="temperature-details-form-winter-external"
            fullWidth
            multiline
            allowNegative
            allowZero
            adornmentProps={{ endAdornment: '\u00b0C' }}
          />
          <NumericTextField
            name="winterInternal"
            control={control}
            required
            label={t('calculation-page.temperature-details-dialog.label-winter-internal')}
            id="temperature-details-form-winter-internal"
            data-testid="temperature-details-form-winter-internal"
            fullWidth
            multiline
            allowNegative
            allowZero
            adornmentProps={{ endAdornment: '\u00b0C' }}
          />
        </Stack>
      </Stack>
    </EditDialog>
  );
}
